import React, { useEffect, useState } from 'react';
import { AddOn, Appointment, Company, Service, Venue, BookingAvailabilitySlot } from '@/types';
import { useAppointmentMutations } from '@features/admin/data';
import { Lock, MessageSquare, Trash2, X } from 'lucide-react';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { HealthAssessmentView } from '@shared/components/HealthAssessmentView';
import { api } from '@/services/api';
import { filterPastAvailabilitySlots, getAvailableTimeSlotsForDate } from '@/lib/scheduling';
import { hasBookingScheduleChange, shouldDisableBookingSave } from '@/lib/bookingEditorSave';
import { normalizeDateOnly } from '@/lib/dates';
import { pickPreferredAvailableTime } from '@/lib/timeSlotSelection';
import { slotTimesForSelection } from '@/lib/bookingSlots';
import { buildMapById } from '@/lib/collections';
import { getAppointmentServiceIds } from '@/lib/appointmentServices';
import {
  buildPaymentAmendWarning,
  estimateAmendedTotal,
  pruneIncompatibleAddOns
} from '@/lib/bookingAmendPricing';
import { getTierPrice } from '@/lib/pricing';
import { normalizeErrorMessage } from '@/lib/errorUtils';

type BookingStatus = Appointment['status'];

function isBookingStatus(value: string): value is BookingStatus {
  return value === 'booked' || value === 'completed' || value === 'cancelled' || value === 'noshow';
}

const sameIdSets = (a: string[] = [], b: string[] = []) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
};

interface BookingEditorProps {
  booking: Appointment;
  venues: Venue[];
  services: Service[];
  addOns: AddOn[];
  companies: {
    id: string;
    name: string;
    requirePo?: boolean;
    tier?: Company['tier'];
    status: Company['status'];
  }[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

export const BookingEditor: React.FC<BookingEditorProps> = ({
  booking: initialBooking,
  venues,
  services,
  addOns,
  companies,
  onClose,
  onSaved,
  onDeleted
}) => {
  const appointmentMutations = useAppointmentMutations();
  const [draft, setDraft] = useState<Appointment>({
    ...initialBooking,
    addOnIds: [...(initialBooking.addOnIds || [])]
  });
  const [original] = useState<Appointment>({
    ...initialBooking,
    addOnIds: [...(initialBooking.addOnIds || [])]
  });
  const [notifyOptions, setNotifyOptions] = useState({ email: true, sms: true });
  const [isDeleting, setIsDeleting] = useState(false);
  const [userChangedScheduleField, setUserChangedScheduleField] = useState(false);

  const [availabilitySlots, setAvailabilitySlots] = useState<BookingAvailabilitySlot[]>([]);
  const [hasAvailabilityData, setHasAvailabilityData] = useState(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [allowOverbook, setAllowOverbook] = useState(false);

  const venuesById = buildMapById(venues);
  const addOnsById = buildMapById(addOns);

  const getBookingDurationMinutes = (b: Appointment) => {
    let duration = 0;
    getAppointmentServiceIds(b).forEach((id) => {
      const service = services.find((s) => s.id === id);
      duration += service?.durationMinutes || 0;
    });
    if (duration <= 0) duration = 30;
    (b.addOnIds || []).forEach((id) => {
      const addon = addOnsById.get(id);
      if (addon) duration += addon.durationMinutes || 0;
    });
    return duration;
  };

  useEffect(() => {
    if (!draft.venueId || !draft.date || !draft.serviceId) {
      setAvailabilitySlots([]);
      setHasAvailabilityData(false);
      setIsLoadingAvailability(false);
      return;
    }
    const dateValue = normalizeDateOnly(draft.date);
    if (!dateValue) {
      setAvailabilitySlots([]);
      setHasAvailabilityData(false);
      setIsLoadingAvailability(false);
      return;
    }
    setAllowOverbook(false);
    let isCurrent = true;
    setIsLoadingAvailability(true);
    setHasAvailabilityData(false);
    api
      .getBookingAvailability({
        venueId: draft.venueId,
        date: dateValue,
        serviceId: draft.serviceId,
        serviceIds: getAppointmentServiceIds(draft),
        addOnIds: draft.addOnIds || [],
        excludeAppointmentId: draft.id
      })
      .then((result) => {
        if (!isCurrent) return;
        const slots = filterPastAvailabilitySlots(dateValue, result?.slots || []);
        setAvailabilitySlots(slots);
        setHasAvailabilityData(true);
        if (userChangedScheduleField) {
          setDraft((prev) => {
            if (!prev) return prev;
            const nextTime = pickPreferredAvailableTime({ currentTime: prev.time, slots });
            return nextTime === prev.time ? prev : { ...prev, time: nextTime };
          });
        }
      })
      .catch(() => {
        if (!isCurrent) return;
        setAvailabilitySlots([]);
        setHasAvailabilityData(false);
      })
      .finally(() => {
        if (isCurrent) setIsLoadingAvailability(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [
    draft.id,
    draft.date,
    draft.venueId,
    draft.serviceId,
    draft.serviceIds,
    draft.addOnIds,
    userChangedScheduleField
  ]);

  const isCancelling = draft.status === 'cancelled';
  const editingCompany = draft.companyId ? companies.find((c) => c.id === draft.companyId) : null;
  const isSageSynced = original.sageSyncStatus === 'synced';
  const companyChanged = (draft.companyId || null) !== (original.companyId || null);
  const companyRemoved = companyChanged && !draft.companyId && Boolean(original.companyId);
  // Only offer active/on_hold companies for (re)assignment, but keep the booking's
  // currently-assigned company visible even if it wouldn't pass that filter (e.g. it
  // was later put on hold/rejected) so the existing assignment can still be seen/cleared.
  const sortedCompanies = [...companies]
    .filter((c) => c.status === 'active' || c.status === 'on_hold' || c.id === original.companyId)
    .sort((a, b) => a.name.localeCompare(b.name));
  const draftServiceIds = getAppointmentServiceIds(draft);
  const serviceChanged = !sameIdSets(getAppointmentServiceIds(original), draftServiceIds);
  const selectedServices = draftServiceIds
    .map((id) => services.find((s) => s.id === id))
    .filter((s): s is Service => Boolean(s));
  const compatibleAddOns = addOns.filter((a) =>
    a.compatibleServiceIds.some((id) => draftServiceIds.includes(id))
  );
  const paymentWarning = (() => {
    if (!serviceChanged && sameIdSets(original.addOnIds, draft.addOnIds)) return null;
    return buildPaymentAmendWarning({
      paymentMethod: draft.paymentMethod,
      paymentStatus: original.paymentStatus,
      originalTotal: original.totalAmount,
      estimatedTotal: estimateAmendedTotal({
        services: selectedServices,
        selectedAddOns: (draft.addOnIds || []).flatMap((id) => {
          const a = addOnsById.get(id);
          return a ? [a] : [];
        }),
        company: (editingCompany ?? null) as Company | null,
        vatRatePercent: original.vatRatePercent
      }),
      currency: original.currency
    });
  })();
  const isPoRequired = Boolean(editingCompany?.requirePo);
  const poMissing = !isCancelling && isPoRequired && !String(draft.poNumber || '').trim();
  const hasAvailableSlots = hasAvailabilityData && availabilitySlots.length > 0;
  const allSlotsFull =
    hasAvailabilityData &&
    availabilitySlots.length > 0 &&
    availabilitySlots.every((s) => !s.available);
  const timeMissing = !isCancelling && !draft.time;
  const scheduleChanged = hasBookingScheduleChange(original, draft);
  const disableSave = shouldDisableBookingSave({
    isLoadingAvailability,
    poMissing,
    isCancelling,
    scheduleChanged,
    timeMissing,
    hasAvailabilityData,
    hasAvailableSlots,
    allowOverbook
  });

  const timeOptions = (() => {
    const dateValue = normalizeDateOnly(draft.date);
    if (!dateValue) return [] as string[];
    const [year, month, day] = dateValue.split('-').map(Number);
    const date = new Date(year, (month || 1) - 1, day || 1);
    if (Number.isNaN(date.getTime())) return [];
    const venue = venuesById.get(draft.venueId);
    const generated = getAvailableTimeSlotsForDate(date, venue?.schedule, {
      requiredDurationMinutes: getBookingDurationMinutes(draft)
    });

    // Shared dropdown slot logic (see @/lib/bookingSlots). When overbooking,
    // fully-booked slots are surfaced too so a time can be picked to save
    // against. The manual-booking CREATE flow uses a button grid instead;
    // keep the two staff overbook flows in sync.
    return slotTimesForSelection({
      slots: availabilitySlots,
      hasAvailabilityData,
      generatedTimes: generated,
      currentTime: draft.time,
      allowOverbook
    });
  })();

  const toggleAddOn = (id: string) => {
    const current = draft.addOnIds || [];
    setDraft({
      ...draft,
      addOnIds: current.includes(id) ? current.filter((a) => a !== id) : [...current, id]
    });
  };

  const handleSave = async () => {
    try {
      await appointmentMutations.update({ ...draft, allowOverbook }, notifyOptions);
      onSaved();
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to update booking.'));
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirmModal(
      `Are you sure you want to delete appointment #${draft.id}? This action is permanent.`,
      {
        title: 'Delete Appointment',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        isDestructive: true
      }
    );
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      await appointmentMutations.remove(draft.id);
      onDeleted();
    } catch (err: unknown) {
      showAlertModal(`Failed to delete booking: ${normalizeErrorMessage(err, 'Unknown error')}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-up border border-slate-200 max-h-[90vh] flex flex-col">
        <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg text-slate-900 uppercase tracking-tight">
              Edit Appointment
            </h3>
            <p className="text-[10px] text-slate-400 font-black uppercase">Ref: {draft.id}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
              Status
            </label>
            <select
              className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none focus:border-teal-500 font-bold"
              value={draft.status}
              onChange={(e) => {
                const next = e.target.value;
                if (!isBookingStatus(next)) return;
                if (next === 'cancelled') {
                  setDraft({ ...original, status: next });
                  return;
                }
                setDraft({ ...draft, status: next });
              }}
            >
              <option value="booked">Booked</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="noshow">No Show</option>
            </select>
            {isCancelling && (
              <p className="text-[10px] text-amber-600 mt-1 font-bold">
                Cancellation ignores appointment detail changes.
              </p>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
              Services
            </label>
            <div className="border-2 border-slate-100 rounded-xl bg-slate-50 p-2 max-h-48 overflow-y-auto space-y-2">
              {(['Medical', 'Screening', 'Vaccination'] as const).map((category) => {
                const group = services.filter((s) => s.category === category);
                if (group.length === 0) return null;
                return (
                  <div key={category}>
                    <p className="text-[9px] font-black text-slate-400 uppercase px-1 mb-1">
                      {category}
                    </p>
                    {group.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 px-1 py-1 text-sm font-bold cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-teal-600 rounded"
                          disabled={isCancelling}
                          checked={draftServiceIds.includes(s.id)}
                          onChange={(e) => {
                            // Deliberately NOT a schedule-field change: a service amend
                            // keeps the booked slot, so the time must not be re-picked
                            // (and availability must not gate the save).
                            const nextServiceIds = e.target.checked
                              ? [...draftServiceIds, s.id]
                              : draftServiceIds.filter((id) => id !== s.id);
                            if (nextServiceIds.length === 0) return;
                            setDraft({
                              ...draft,
                              serviceId: nextServiceIds[0],
                              serviceIds: nextServiceIds,
                              addOnIds: pruneIncompatibleAddOns(
                                draft.addOnIds || [],
                                addOns,
                                nextServiceIds
                              )
                            });
                          }}
                        />
                        <span>
                          {s.name} — {s.durationMinutes} min — £
                          {getTierPrice(s, {
                            company: (editingCompany ?? null) as Company | null,
                            isCorporate: Boolean(editingCompany)
                          }).toFixed(2)}
                        </span>
                      </label>
                    ))}
                  </div>
                );
              })}
              {selectedServices.length === 0 && (
                <p className="text-xs text-slate-500 px-1">
                  {draft.serviceName || 'Current service (unavailable)'}
                </p>
              )}
            </div>
            {serviceChanged && (
              <p className="text-[10px] text-amber-600 mt-1 font-bold">
                Changing the services updates the required questionnaires and recalculates the price
                on save.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                Date
              </label>
              <input
                type="date"
                className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                value={normalizeDateOnly(draft.date)}
                onChange={(e) => {
                  setUserChangedScheduleField(true);
                  setDraft({ ...draft, date: e.target.value });
                }}
                disabled={isCancelling}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                Time
              </label>
              <select
                className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                value={draft.time}
                onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                disabled={isLoadingAvailability || isCancelling}
              >
                {isLoadingAvailability && <option value="">Loading availability...</option>}
                {!isCancelling && !isLoadingAvailability && timeOptions.length === 0 && (
                  <option value="">No available slots</option>
                )}
                {!isCancelling &&
                  !isLoadingAvailability &&
                  timeOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                {isCancelling && (
                  <option value={draft.time || ''}>{draft.time || 'No time selected'}</option>
                )}
              </select>
              {!isLoadingAvailability &&
                !isCancelling &&
                scheduleChanged &&
                hasAvailabilityData &&
                availabilitySlots.length === 0 && (
                  <p className="text-[10px] text-red-600 font-bold mt-1">
                    No available slots for this date/service. Choose another date or venue.
                  </p>
                )}
              {!isLoadingAvailability && !isCancelling && scheduleChanged && allSlotsFull && (
                <div className="mt-1 space-y-1">
                  <p className="text-[10px] text-red-600 font-bold">
                    All slots are fully booked for this date/service.
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      checked={allowOverbook}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAllowOverbook(checked);
                        if (checked && !draft.time) {
                          const fallbackTime = availabilitySlots[0]?.time || '';
                          if (fallbackTime) setDraft({ ...draft, time: fallbackTime });
                        }
                      }}
                    />
                    <span className="text-[11px] font-bold text-slate-600">Allow overbook</span>
                  </label>
                  {allowOverbook && (
                    <p className="text-[11px] text-red-500">
                      Overbooking enabled: this slot is fully booked but will be saved anyway.
                    </p>
                  )}
                </div>
              )}
              {timeMissing && (
                <p className="text-[10px] text-amber-600 mt-1 font-bold">
                  Select an available time to continue.
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
              Location
            </label>
            <select
              className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
              value={draft.venueId}
              onChange={(e) => {
                setUserChangedScheduleField(true);
                setDraft({ ...draft, venueId: e.target.value });
              }}
              disabled={isCancelling}
            >
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
              Job Title
            </label>
            <input
              type="text"
              className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
              value={draft.customerJobTitle || ''}
              onChange={(e) => setDraft({ ...draft, customerJobTitle: e.target.value })}
              disabled={isCancelling}
              placeholder="e.g. Crane Operator"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Printed on the certificate for this appointment.
            </p>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
              Company
            </label>
            <select
              className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
              value={draft.companyId || ''}
              onChange={(e) => setDraft({ ...draft, companyId: e.target.value || null })}
              disabled={isCancelling || isSageSynced}
            >
              <option value="">Individual (no company)</option>
              {sortedCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {isSageSynced && (
              <p className="text-[10px] text-slate-400 mt-1">
                Invoiced to Sage — the company can no longer be changed.
              </p>
            )}
            {!isSageSynced && companyChanged && (
              <p className="text-[10px] text-amber-600 mt-1 font-bold">
                {companyRemoved
                  ? "Price stays as originally paid. The booking will no longer appear in the company's portal."
                  : "Price stays as originally paid. The booking will appear in the company's portal."}
              </p>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
              PO Number {isPoRequired ? '(Required)' : '(Optional)'}
            </label>
            <input
              type="text"
              className={`w-full border-2 p-2.5 rounded-xl outline-none text-sm ${poMissing ? 'border-red-200 bg-red-50' : 'border-slate-100 bg-slate-50'}`}
              value={draft.poNumber || ''}
              onChange={(e) => setDraft({ ...draft, poNumber: e.target.value })}
              disabled={isCancelling}
              placeholder={isPoRequired ? 'PO Number required for this company' : 'Enter PO number'}
            />
            {poMissing && (
              <p className="text-[10px] text-red-600 mt-1 font-bold">
                PO Number is required for this company.
              </p>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">
              Add-ons
            </label>
            {compatibleAddOns.length === 0 ? (
              <p className="text-xs text-slate-400">No add-ons available for this service.</p>
            ) : (
              <div className="space-y-2">
                {compatibleAddOns.map((addon) => (
                  <label
                    key={addon.id}
                    className="flex items-center gap-2 text-xs font-bold text-slate-600"
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-teal-600 rounded"
                      checked={(draft.addOnIds || []).includes(addon.id)}
                      disabled={isCancelling}
                      onChange={() => toggleAddOn(addon.id)}
                    />
                    <span>{addon.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1 flex items-center gap-2">
              <MessageSquare className="w-3 h-3 text-slate-400" /> Technician-only note
            </label>
            <textarea
              rows={3}
              className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
              value={draft.staffPrivateNote || ''}
              onChange={(e) => setDraft({ ...draft, staffPrivateNote: e.target.value })}
              disabled={isCancelling}
              placeholder="Private note for the technician (not visible to the customer)."
            />
            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Visible to technicians only.
            </p>
          </div>

          {paymentWarning && (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl">
              <p className="text-[10px] font-black text-amber-800 uppercase mb-1">
                Payment mismatch
              </p>
              <p className="text-xs font-bold text-amber-700">{paymentWarning}</p>
              <p className="text-[10px] text-amber-600 mt-1">
                The final price is recalculated on save; the card payment is not adjusted
                automatically.
              </p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-100 p-4 rounded-2xl space-y-2">
            <p className="text-[10px] font-black text-blue-800 uppercase mb-2">
              Notification Dispatch
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 rounded"
                checked={notifyOptions.email}
                onChange={(e) => setNotifyOptions({ ...notifyOptions, email: e.target.checked })}
              />
              <span className="text-xs font-bold text-blue-700">Send Email Update</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 rounded"
                checked={notifyOptions.sms}
                onChange={(e) => setNotifyOptions({ ...notifyOptions, sms: e.target.checked })}
              />
              <span className="text-xs font-bold text-blue-700">Send SMS Confirmation</span>
            </label>
          </div>

          {initialBooking.healthAssessment && (
            <div className="border-t border-slate-100 pt-6 mt-6">
              <HealthAssessmentView data={initialBooking.healthAssessment} />
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-slate-50 flex justify-between gap-3">
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="p-2 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all disabled:opacity-50"
            title="Delete Record"
          >
            <Trash2 className="w-5 h-5" />
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-6 py-2 text-slate-500 font-bold hover:bg-white rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={disableSave}
              className="bg-teal-600 text-white px-8 py-2 rounded-xl font-black hover:bg-teal-700 shadow-lg shadow-teal-600/20 active:scale-95 transition-all text-xs uppercase tracking-widest disabled:opacity-50"
            >
              Update Booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
